import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetFootprintingDairyManureAdditive,
  useGetFootprintingDairyPrimaryDataById,
  useGetManureAdditives,
  useKeyedFormList,
  useMutateFootprintingDairyManureAdditive,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import {
  ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE,
  ENUM_MANURE_ADDITIVE_UNITS,
  FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE,
  UploadableManureAdditive,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Typography,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const feedAdditivePlaceholder: UploadableManureAdditive = {
  id: undefined,
  manureAdditiveId: "",
  amountUsedM3: null,
  amountUsedM3Unit: null,
  enrichedSlurryPct: null,
};

const ManureAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [manureAdditiveActive, setManureAdditiveActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE | null>(null);

  const dairyPrimaryDataFullQuery = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: dairyPrimaryDataQuery } = dairyPrimaryDataFullQuery;
  const manureAdditivesFullQuery = useGetFootprintingDairyManureAdditive(primaryDataId);
  const { data: manureAdditivesQuery } = manureAdditivesFullQuery;
  const { data: dairyManureAdditivesQuery } = useGetManureAdditives();
  const isLoading = [dairyPrimaryDataFullQuery, manureAdditivesFullQuery].some(queryIsLoading);

  const keyedManureAdditives = useKeyedFormList<UploadableManureAdditive>(formRef, { isReady: !isLoading });

  const selectGroupName = "footprinting-dairy-manure-additive-select";

  const { mutate: mutateDairyManureAdditives } = useMutateFootprintingDairyManureAdditive(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedManureAdditive: keyedManureAdditives.payload.filter((m) => !!m.manureAdditiveId),
    },
    primaryDataId
  );

  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      manureAdditiveActive,
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData({});
        mutateDairyManureAdditives();
      }
    },
    [isLoading, mutateDairyPrimaryData, mutateDairyManureAdditives, readOnly]
  );

  // Load feed additives data
  useEffect(() => {
    if (manureAdditivesQuery) {
      keyedManureAdditives.actions.hydrate(
        manureAdditivesQuery.length
          ? manureAdditivesQuery.map((m) => ({
              id: m.id,
              manureAdditiveId: m.manureAdditiveId ?? "",
              amountUsedM3: m.amountUsedM3 ?? null,
              amountUsedM3Unit: m.amountUsedM3Unit ?? null,
              enrichedSlurryPct: m.enrichedSlurryPct ?? null,
            }))
          : [feedAdditivePlaceholder]
      );
    }
  }, [manureAdditivesQuery, keyedManureAdditives.actions]);

  // Load primary data
  useEffect(() => {
    if (dairyPrimaryDataQuery) {
      setManureAdditiveActive(dairyPrimaryDataQuery.manureAdditiveActive ?? null);

      formRef.current?.setFieldsValue({
        manureAdditiveActive: dairyPrimaryDataQuery.manureAdditiveActive ?? null,
      });

      if (dairyPrimaryDataQuery.manureAdditiveActive !== null && dairyPrimaryDataQuery.manureAdditiveActive !== undefined) {
        keyedManureAdditives.actions.requestRehydrationValidation();
      }
    }
  }, [dairyPrimaryDataQuery, keyedManureAdditives.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-dairy-manure-additives-form" className="padding-200">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "64%" }}>
              {t("dairyManureAdditiveForm.manureAdditivesQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              name="manureAdditiveActive"
              dataTestId="footprinting-dairy-manure-additives-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setManureAdditiveActive(target.value);
                if (target.value === FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES && keyedManureAdditives.entries.length === 0) {
                  keyedManureAdditives.actions.add(feedAdditivePlaceholder);
                }
              }}
              value={manureAdditiveActive}
              defaultValue={manureAdditiveActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES, FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.NO].map((manureAdditiveActive) => ({
                label: t(`dairyManureAdditiveForm.manureAdditiveOptions.${manureAdditiveActive}`),
                value: manureAdditiveActive,
              }))}
            />
          </Flex>
          {manureAdditiveActive === FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES &&
            keyedManureAdditives.entries.map((additive, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;

              return (
                <Flex key={additive.clientKey} vertical gap={16}>
                  <Flex justify="space-between" align="center" vertical={false}>
                    <Typography variant="h5" style={{ flex: 1 }}>
                      {t("dairyManureAdditiveForm.additiveNumber", { count: index + 1 })}
                    </Typography>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      dataTestId={`delete-manure-additive-${index}`}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => {
                        keyedManureAdditives.actions.remove(additive.clientKey);
                        if (keyedManureAdditives.entries.length === 1) setManureAdditiveActive(null);
                      }}
                    />
                  </Flex>
                  <Select
                    disabled={readOnly}
                    name={`${selectGroupName}-${additive.clientKey}`}
                    dataTestId={selectGroupWithIndex}
                    label={t("dairyManureAdditiveForm.question.manureAdditiveTypeQuestionInput")}
                    rules={[{ required: true, message: "" }]}
                    initialValue={dairyManureAdditivesQuery?.find((f) => f.id === additive.manureAdditiveId)?.name || ""}
                    value={dairyManureAdditivesQuery?.find((f) => f.id === additive.manureAdditiveId)?.name || ""}
                    onChange={(v) => {
                      const matchingPractice = dairyManureAdditivesQuery?.find((f) => f.name === v);
                      keyedManureAdditives.actions.update(additive.clientKey, { manureAdditiveId: matchingPractice ? matchingPractice.id : "" });
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                  >
                    {(dairyManureAdditivesQuery ?? []).map((dairyManureAdditive) => {
                      // Check if this additive ID is already used in another item
                      const isAlreadyUsed = keyedManureAdditives.entries.some(
                        (a) => a.manureAdditiveId === dairyManureAdditive.id && a.manureAdditiveId !== additive.manureAdditiveId
                      );

                      return (
                        <Option
                          disabled={readOnly || isAlreadyUsed}
                          key={`${selectGroupWithIndex}-${dairyManureAdditive.id}`}
                          data-testid={`${selectGroupWithIndex}-${dairyManureAdditive.id}`}
                          value={dairyManureAdditive.name}
                        >
                          {dairyManureAdditive.name}
                        </Option>
                      );
                    })}
                  </Select>

                  <InputNumberWithUnitSelector
                    key={`amount-${additive.clientKey}-${additive.manureAdditiveId}`}
                    className="floating-label"
                    disabled={readOnly}
                    dataTestId={`footprinting-dairy-manure-additive-amount-input-${index}`}
                    name={`amountUsedM3-${additive.clientKey}`}
                    label={t("dairyManureAdditiveForm.question.amountUsedQuestionInput")}
                    unitLabel={t("unit", { ns: "footprinting" })}
                    units={["kilograms", "liters"]}
                    unitDisplayOverride={{
                      kilograms: "kg / m³",
                      liters: "l / m³",
                    }}
                    value={additive.amountUsedM3}
                    initialValue={additive.amountUsedM3}
                    unitValue={additive.amountUsedM3Unit}
                    onChange={(v, unit) =>
                      keyedManureAdditives.actions.update(additive.clientKey, { amountUsedM3: v, amountUsedM3Unit: unit as ENUM_MANURE_ADDITIVE_UNITS })
                    }
                    validateFirst
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("dairyManureAdditiveForm.error.amountUsedError")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />

                  <InputNumber
                    disabled={readOnly}
                    name={`enrichedSlurryPct-${additive.clientKey}`}
                    dataTestId="footprinting-manure-enriched-slurry-input"
                    label={t("dairyManureAdditiveForm.question.enrichedSlurryQuestionInput")}
                    suffix="%"
                    value={formatFractionInput(additive.enrichedSlurryPct)}
                    initialValue={formatFractionInput(additive.enrichedSlurryPct)}
                    onChange={(v) => keyedManureAdditives.actions.update(additive.clientKey, { enrichedSlurryPct: formatPercentageInput(v) })}
                    validateFirst
                    rules={[
                      { required: true, message: t("dairyManureAdditiveForm.error.validPercentage") },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                            return Promise.reject(new Error(t("dairyManureAdditiveForm.error.validPercentage")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Flex>
              );
            })}
          {manureAdditiveActive === FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES && (
            <Button
              type="text"
              dataTestId="add-manure-additive-button"
              disabled={readOnly}
              onClick={() => {
                keyedManureAdditives.actions.add(feedAdditivePlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyManureAdditiveForm.add")}
            </Button>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

ManureAdditiveForm.Key = "dairy.manureAdditive";
export { ManureAdditiveForm };
